import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { startAutoDeposit } from "@/lib/payments.functions";
import { AppHeader } from "@/components/AppHeader";
import { useProfile } from "@/lib/auth";
import { useAppSettings } from "@/lib/settings";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  Wallet as WalletIcon,
  Plus,
  ArrowDown,
  CheckCircle2,
  Clock,
  XCircle,
  RotateCcw,
  MessageCircle,
  Loader2,
  Send,
} from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { usePageLoadMetric } from "@/lib/diagnostics/usePageLoadMetric";
import { rememberPendingOrder, readPendingOrder } from "@/lib/payment-browser";


export const Route = createFileRoute("/_authenticated/wallet")({
  head: () => ({ meta: [{ title: "Wallet — Brothers Club" }] }),
  component: WalletPage,
});

function WalletPage() {
  const { data: profile } = useProfile();
  usePageLoadMetric("wallet_load", !profile);
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [wdOpen, setWdOpen] = useState(false);

  const { data: txns } = useQuery({
    queryKey: ["my_txns", profile?.id],
    enabled: !!profile,
    queryFn: async () => {
      const { data } = await supabase
        .from("wallet_transactions")
        .select("*")
        .eq("user_id", profile!.id)
        .order("created_at", { ascending: false })
        .limit(50);
      return data ?? [];
    },
  });

  const betIds = new Set<string>();
  const gameIds = new Set<string>();
  for (const t of (txns ?? []) as any[]) {
    const meta: any = t.meta ?? {};
    if (meta.game_id) gameIds.add(meta.game_id);
    if (meta.bet_id) betIds.add(meta.bet_id);
    if (Array.isArray(meta.bet_ids)) meta.bet_ids.forEach((id: string) => id && betIds.add(id));
  }

  const { data: betsMap } = useQuery({
    queryKey: ["my_txn_bets", profile?.id, Array.from(betIds).sort().join(",")],
    enabled: !!profile && betIds.size > 0,
    queryFn: async () => {
      const { data } = await supabase
        .from("bets")
        .select("id, game_id, number, amount, status, payout")
        .in("id", Array.from(betIds));
      const m: Record<string, any> = {};
      for (const b of (data ?? []) as any[]) {
        m[b.id] = b;
        if (b.game_id) gameIds.add(b.game_id);
      }
      return m;
    },
  });

  const { data: gamesMap } = useQuery({
    queryKey: ["my_txn_games", Array.from(gameIds).sort().join(",")],
    enabled: gameIds.size > 0,
    queryFn: async () => {
      const { data } = await supabase.from("games").select("id, name").in("id", Array.from(gameIds));
      const m: Record<string, string> = {};
      for (const g of (data ?? []) as any[]) m[g.id] = g.name;
      return m;
    },
  });

  const mainBal = Number(profile?.wallet_balance ?? 0);
  const winBal = Number((profile as any)?.winning_balance ?? 0);

  return (
    <div>
      <AppHeader />
      <div className="gradient-header px-4 pb-6 -mt-1">
        <div className="rounded-2xl bg-white/15 p-5 backdrop-blur text-primary-foreground">
          <p className="text-xs uppercase tracking-wide text-white/70">Total Balance</p>
          <div className="mt-1 flex items-center justify-between">
            <p className="text-3xl font-black">
              ₹ {(mainBal + winBal).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
            </p>
            <WalletIcon className="h-10 w-10 opacity-80" />
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
            <div className="rounded-xl bg-white/10 px-3 py-2">
              <p className="text-white/60 uppercase tracking-wide">Main Balance</p>
              <p className="mt-0.5 text-base font-black">₹{mainBal.toLocaleString("en-IN")}</p>
              <p className="mt-0.5 text-[10px] text-white/60">Deposits • used for bets first</p>
            </div>
            <div className="rounded-xl bg-white/15 px-3 py-2 ring-1 ring-white/25">
              <p className="text-white/80 uppercase tracking-wide">Withdrawable</p>
              <p className="mt-0.5 text-base font-black">₹{winBal.toLocaleString("en-IN")}</p>
              <p className="mt-0.5 text-[10px] text-white/70">Winnings • can be withdrawn</p>
            </div>
          </div>
          <p className="mt-2 text-center text-[10px] text-white/60">
            Only Winnings can be withdrawn. Main Balance is deposits — used for playing only.
          </p>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <button className="flex items-center justify-center gap-2 rounded-xl bg-white/90 py-2.5 text-sm font-bold text-primary">
                  <Plus className="h-4 w-4" /> Add Money
                </button>
              </DialogTrigger>
              <AddMoneyDialog onClose={() => setAddOpen(false)} />
            </Dialog>
            <Dialog open={wdOpen} onOpenChange={setWdOpen}>
              <DialogTrigger asChild>
                <button className="flex items-center justify-center gap-2 rounded-xl bg-white/15 py-2.5 text-sm font-bold text-white">
                  <ArrowDown className="h-4 w-4" /> Withdraw
                </button>
              </DialogTrigger>
              <WithdrawDialog
                onClose={() => {
                  setWdOpen(false);
                  qc.invalidateQueries();
                }}
              />
            </Dialog>
          </div>
        </div>
      </div>

      <div className="px-4 py-4 space-y-4">
        <PendingAutoDepositCard />
        <div>

          <h3 className="mb-2 text-sm font-bold">Transaction History</h3>
          <div className="space-y-2">
            {(txns ?? []).map((t) => (
              <TxnRow key={t.id} t={t} betsMap={betsMap ?? {}} gamesMap={gamesMap ?? {}} />
            ))}
            {(!txns || txns.length === 0) && (
              <div className="rounded-2xl bg-card p-8 text-center text-sm text-muted-foreground shadow-card">
                No transactions yet.
              </div>
            )}
          </div>
        </div>
        <div className="h-4" />
      </div>
    </div>
  );
}



/**
 * Shows an unfinished automatic (IMB) deposit so a user who closed the app
 * mid-payment can resume checking its status. Status is always verified
 * server-side — this card never credits anything.
 */
function PendingAutoDepositCard() {
  const [orderId, setOrderId] = useState<string | null>(null);

  useEffect(() => {
    setOrderId(readPendingOrder());
  }, []);

  if (!orderId) return null;

  return (
    <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4">
      <p className="text-sm font-bold">Payment in progress</p>
      <p className="mt-0.5 text-xs text-muted-foreground">
        Order ID: <span className="font-semibold">{orderId}</span>
      </p>
      <Button asChild className="mt-3 h-10 w-full" variant="secondary">
        <Link to="/payment/return" search={{ order: orderId }}>
          Check Payment Status
        </Link>
      </Button>
    </div>
  );
}

function getLinkedBets(t: any, betsMap: Record<string, any>) {
  const meta = t.meta ?? {};



  const ids: string[] = Array.isArray(meta.bet_ids) ? meta.bet_ids : meta.bet_id ? [meta.bet_id] : [];
  return ids.map((id) => betsMap[id]).filter(Boolean);
}

function getBetMarketName(t: any, bets: any[], gamesMap: Record<string, string>) {
  const meta = t.meta ?? {};
  return (meta.game_id && gamesMap[meta.game_id]) || (bets[0]?.game_id && gamesMap[bets[0].game_id]) || "Unknown Market";
}

function getBetEntryStatus(bets: any[]): "Pending" | "Won" | "Lost" | "Partial Win" {
  if (bets.length === 0) return "Pending";
  const hasPending = bets.some((b) => b.status === "pending");
  const hasWon = bets.some((b) => b.status === "won" || Number(b.payout || 0) > 0);
  const hasLost = bets.some((b) => b.status === "lost");
  if (hasPending) return "Pending";
  if (hasWon && hasLost) return "Partial Win";
  if (hasWon) return "Won";
  return "Lost";
}

function betEntryStatusColor(status: "Pending" | "Won" | "Lost" | "Partial Win") {
  return status === "Won" ? "text-success" : status === "Pending" ? "text-warning" : status === "Partial Win" ? "text-primary" : "text-destructive";
}

function BetDetails({ t, betsMap, gamesMap }: { t: any; betsMap: Record<string, any>; gamesMap: Record<string, string> }) {
  const bets = getLinkedBets(t, betsMap);
  const gameName =
    getBetMarketName(t, bets, gamesMap);
  const totalFromBets = bets.reduce((sum, b) => sum + Number(b.amount || 0), 0);
  const totalBet = totalFromBets > 0 ? totalFromBets : Math.abs(Number(t.amount || 0));
  const statusText = getBetEntryStatus(bets);
  return (
    <div className="rounded-2xl bg-card p-4 shadow-card space-y-2">
      <p className="text-sm font-semibold text-foreground">Game Entry</p>
      <p className="text-xl font-black text-primary uppercase">{gameName}</p>
      <p className="text-sm font-semibold text-foreground">Bet: ₹{totalBet.toLocaleString("en-IN")}</p>
      <p className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString("en-IN")}</p>
      <p className={`text-xs font-bold ${betEntryStatusColor(statusText)}`}>Status: {statusText}</p>
    </div>
  );
}

function WinDetails({ t, betsMap, gamesMap }: { t: any; betsMap: Record<string, any>; gamesMap: Record<string, string> }) {
  const meta = t.meta ?? {};
  const bet = meta.bet_id ? betsMap[meta.bet_id] : null;
  const gameName = (meta.game_id && gamesMap[meta.game_id]) || (bet?.game_id && gamesMap[bet.game_id]) || "Game";
  const num = meta.result ?? bet?.number;
  return (
    <div className="mt-2 rounded-xl bg-success/10 p-2 text-[11px] leading-tight">
      <div className="font-semibold">{gameName}</div>
      <div className="mt-0.5 text-muted-foreground">
        Winning Number:{" "}
        <span className="font-mono font-semibold text-foreground">
          {num != null ? String(num).padStart(2, "0") : "--"}
        </span>
        {meta.multiplier ? (
          <>
            {" "}
            • Multiplier: <span className="font-semibold text-foreground">×{meta.multiplier}</span>
          </>
        ) : null}
      </div>
      {bet && (
        <div className="text-muted-foreground">
          Bet Amount:{" "}
          <span className="font-semibold text-foreground">₹{Number(bet.amount).toLocaleString("en-IN")}</span>
        </div>
      )}
    </div>
  );
}

function TxnRow({ t, betsMap, gamesMap }: { t: any; betsMap: Record<string, any>; gamesMap: Record<string, string> }) {
  if (t.type === "bet") {
    return <BetDetails t={t} betsMap={betsMap} gamesMap={gamesMap} />;
  }

  const isCredit = ["add", "win", "refund", "late_bet_refund", "referral_bonus", "admin_credit"].includes(t.type);
  const label: Record<string, string> = {
    add: "Added Money",
    withdraw: "Withdraw",
    win: "Winning Amount",
    bet: "Game Entry",
    refund: "Refund",
    late_bet_refund: "Late Bet Refund",
    referral_bonus: "Referral Bonus",
    admin_credit: "Admin Credit",
    admin_debit: "Admin Debit",
  };
  const StatusIcon =
    t.status === "success" ? CheckCircle2 : t.status === "pending" ? Clock : t.status === "refunded" ? RotateCcw : XCircle;
  const statusLabel =
    t.status === "success" ? "Approved" : t.status === "refunded" ? "Refunded" : t.status === "rejected" ? "Rejected" : "Pending";
  const statusColor =
    t.status === "success" ? "text-success" : t.status === "pending" ? "text-warning" : t.status === "refunded" ? "text-primary" : "text-destructive";
  return (
    <div className="rounded-2xl bg-card p-3 shadow-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span
            className={`grid h-10 w-10 place-items-center rounded-full ${
              isCredit ? "bg-success/15 text-success" : "bg-destructive/15 text-destructive"
            }`}
          >
            {isCredit ? <Plus className="h-5 w-5" /> : <ArrowDown className="h-5 w-5" />}
          </span>
          <div>
            <p className="text-sm font-semibold">{label[t.type] ?? t.type}</p>
            <p className="text-[11px] text-muted-foreground">{new Date(t.created_at).toLocaleString("en-IN")}</p>
            {t.status === "rejected" && t.meta?.reject_reason && (
              <p className="mt-0.5 text-[10px] text-destructive">Reason: {t.meta.reject_reason}</p>
            )}
          </div>
        </div>
        <div className="text-right">
          <p className={`text-sm font-black ${isCredit ? "text-success" : "text-destructive"}`}>
            {isCredit ? "+" : "-"}₹{Number(t.amount).toLocaleString("en-IN")}
          </p>
          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase ${statusColor}`}>
            <StatusIcon className="h-3 w-3" /> {statusLabel}
          </span>
        </div>
      </div>
      {t.type === "win" && <WinDetails t={t} betsMap={betsMap} gamesMap={gamesMap} />}
    </div>
  );
}

function buildDepositMessage(opts: { userId: string; phone: string; amount: number }) {
  const lines = [
    "Hi, I would like to make a deposit to my Brothers Club wallet.",
    "",
    `User ID: ${opts.userId}`,
    `Mobile Number: ${opts.phone || "(not set)"}`,
    `Deposit Request: ₹${opts.amount}`,
    "",
    "I will send the payment screenshot / UTR after your reply.",
  ];
  return lines.join("\n");
}

function normalizeTelegramLink(raw: string): string | null {
  const v = (raw || "").trim();
  if (!v) return null;
  if (/^https?:\/\//i.test(v)) return v;
  const handle = v.replace(/^@/, "");
  if (!handle) return null;
  return `https://t.me/${handle}`;
}

function normalizeWhatsappNumber(raw: string): string | null {
  const digits = (raw || "").replace(/\D/g, "");
  return digits || null;
}

function AddMoneyDialog({ onClose }: { onClose: () => void }) {
  const { data: settings } = useAppSettings();
  const { data: profile } = useProfile();
  const navigate = useNavigate();
  const startAuto = useServerFn(startAutoDeposit);
  const [amount, setAmount] = useState("");
  const [starting, setStarting] = useState(false);
  const [autoError, setAutoError] = useState<string | null>(null);
  const [forceManual, setForceManual] = useState(false);

  const contact = settings?.payment_contact;
  const telegramEnabled = !!contact?.telegram_enabled && !!normalizeTelegramLink(contact?.telegram_link ?? "");
  const whatsappEnabled = !!contact?.whatsapp_enabled && !!normalizeWhatsappNumber(contact?.whatsapp_number ?? "");
  const anyEnabled = telegramEnabled || whatsappEnabled;

  const gateway = settings?.imb_gateway;
  const automatic = settings?.deposit_mode?.mode === "automatic" && !!gateway?.enabled && !forceManual;
  const showManual = !automatic || (gateway?.manual_fallback !== false && !!autoError);

  const DEPOSIT_MIN = settings?.wallet.min_add ?? 100;
  const DEPOSIT_MAX = settings?.wallet.max_add ?? 50000;
  // In automatic mode the gateway limits are authoritative (they mirror the server-side check).
  const min = automatic && gateway?.min_amount ? gateway.min_amount : DEPOSIT_MIN;
  const max = automatic && gateway?.max_amount ? gateway.max_amount : DEPOSIT_MAX;
  const amountNum = Number(amount || 0);
  const amountValid = amountNum >= min && amountNum <= max;

  async function payAutomatically() {
    if (!profile) return toast.error("Please sign in again.");
    if (!amountValid) return toast.error(`Amount must be between ₹${min} and ₹${max}.`);
    setStarting(true);
    setAutoError(null);
    try {
      const res = await startAuto({
        data: {
          amount: amountNum,
          mobile: String((profile as any).phone ?? ""),
          origin: window.location.origin,
        },
      });
      if (!res.ok) {
        setAutoError(res.message || gateway?.maintenance_message || "Automatic payment is unavailable right now.");
        if (!res.fallbackManual) toast.error(res.message);
        return;
      }
      onClose();
      // IMB appends no return-URL parameters, so keep a local reference.
      rememberPendingOrder(res.internalOrderId);
      const { openHostedPayment } = await import("@/lib/payment-browser");
      const opened = await openHostedPayment(res.paymentUrl, res.internalOrderId);
      if (!opened) {
        setAutoError("The payment link returned by the gateway was rejected for security reasons.");
        return;
      }
      // Park the app on the status screen so the return lands somewhere useful.
      navigate({ to: "/payment/return", search: { order: res.internalOrderId } });


    } catch {
      setAutoError(gateway?.maintenance_message || "Automatic payment is unavailable right now.");
    } finally {
      setStarting(false);
    }
  }

  function openContact(kind: "telegram" | "whatsapp") {
    if (!profile) return toast.error("Please sign in again.");
    if (!amountValid) return toast.error(`Amount must be between ₹${min} and ₹${max}.`);
    const message = buildDepositMessage({
      userId: profile.id,
      phone: String((profile as any).phone ?? ""),
      amount: amountNum,
    });
    const encoded = encodeURIComponent(message);
    let url = "";
    if (kind === "telegram") {
      const base = normalizeTelegramLink(contact?.telegram_link ?? "");
      if (!base) return toast.error("Telegram is not configured.");
      url = `${base}${base.includes("?") ? "&" : "?"}text=${encoded}`;
    } else {
      const num = normalizeWhatsappNumber(contact?.whatsapp_number ?? "");
      if (!num) return toast.error("WhatsApp is not configured.");
      url = `https://wa.me/${num}?text=${encoded}`;
    }
    try {
      window.open(url, "_blank");
    } catch {
      window.location.href = url;
    }
    onClose();
  }

  return (
    <DialogContent className="max-w-sm rounded-3xl">
      <DialogHeader>
        <DialogTitle>Add Money</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label>Amount (₹)</Label>
          <Input
            inputMode="numeric"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))}
            placeholder={`${min} - ${max}`}
            className="h-12 text-lg"
          />
          {amount && !amountValid && (
            <p className="mt-1 text-[11px] text-destructive">
              {amountNum < min ? `Minimum deposit is ₹${min}.` : `Maximum deposit is ₹${max}.`}
            </p>
          )}
          <div className="mt-2 grid grid-cols-4 gap-2">
            {[500, 1000, 2000, 5000].map((v) => (
              <button
                key={v}
                onClick={() => setAmount(String(v))}
                className="rounded-lg border border-input py-2 text-xs font-semibold hover:bg-accent"
              >
                ₹{v}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-primary/20 bg-primary/5 p-3 text-[11px] text-muted-foreground">
          {automatic
            ? "Pay instantly with any UPI app. Your wallet is credited automatically once the payment is confirmed."
            : "Tap an option below to contact the admin with a prefilled deposit message. After you send the payment screenshot, the admin will credit your wallet manually."}
        </div>

        {automatic && (
          <Button
            onClick={payAutomatically}
            disabled={!amountValid || starting}
            className="h-12 w-full justify-center gap-2 gradient-primary"
          >
            {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Pay ₹{amountNum || 0} with UPI
          </Button>
        )}

        {autoError && (
          <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-[11px]">
            {autoError}
            {gateway?.manual_fallback !== false && anyEnabled && (
              <span className="block mt-1 text-muted-foreground">You can still deposit manually below.</span>
            )}
          </div>
        )}

        {showManual && !anyEnabled && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
            Deposit contact channels are not configured yet. Please contact support.
          </div>
        )}

        {showManual && (
          <div className="space-y-2">
            {telegramEnabled && (
              <Button
                onClick={() => openContact("telegram")}
                disabled={!amountValid}
                className="h-12 w-full justify-center gap-2 bg-[#229ED9] text-white hover:bg-[#1b87ba]"
              >
                <Send className="h-4 w-4" /> Contact on Telegram
              </Button>
            )}
            {whatsappEnabled && (
              <Button
                onClick={() => openContact("whatsapp")}
                disabled={!amountValid}
                className="h-12 w-full justify-center gap-2 bg-[#25D366] text-white hover:bg-[#1fb457]"
              >
                <MessageCircle className="h-4 w-4" /> Contact on WhatsApp
              </Button>
            )}
          </div>
        )}

        {automatic && !autoError && anyEnabled && gateway?.manual_fallback !== false && (
          <button
            onClick={() => setForceManual(true)}
            className="w-full text-center text-[11px] text-muted-foreground underline"
          >
            Having trouble? Deposit manually instead
          </button>
        )}

        <p className="text-center text-[10px] text-muted-foreground">
          {automatic
            ? "Wallet is credited only after the payment is verified with the gateway."
            : "Wallet is credited only after admin verifies your payment."}
        </p>
      </div>
    </DialogContent>
  );
}


function WithdrawDialog({ onClose }: { onClose: () => void }) {
  const { data: settings } = useAppSettings();
  const { data: profile } = useProfile();
  const [amount, setAmount] = useState("");
  const [upiId, setUpiId] = useState("");
  const [utr, setUtr] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [bankName, setBankName] = useState("");
  const [accountName, setAccountName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [ifsc, setIfsc] = useState("");
  const [loading, setLoading] = useState(false);

  const min = settings?.wallet.min_withdraw ?? 200;
  const max = settings?.wallet.max_withdraw ?? 25000;
  const amountNum = Number(amount || 0);
  const amountValid = amountNum >= min && amountNum <= max;

  const wd = settings?.withdraw;
  const withdrawEnabled = wd?.enabled !== false;
  const wdOpenStr = (wd?.open_time || "").slice(0, 5);
  const wdCloseStr = (wd?.close_time || "").slice(0, 5);
  function toMin(s: string): number | null {
    const m = /^(\d{1,2}):(\d{2})/.exec(s || "");
    if (!m) return null;
    const h = Number(m[1]);
    const mm = Number(m[2]);
    if (isNaN(h) || isNaN(mm)) return null;
    return h * 60 + mm;
  }
  function istNowMin(): number {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(new Date());
    const h = Number(parts.find((p) => p.type === "hour")?.value ?? "0");
    const m = Number(parts.find((p) => p.type === "minute")?.value ?? "0");
    return h * 60 + m;
  }
  function isWithdrawInWindow(): boolean {
    const o = toMin(wdOpenStr);
    const c = toMin(wdCloseStr);
    if (o == null || c == null) return true;
    const n = istNowMin();
    return c >= o ? n >= o && n < c : n >= o || n < c;
  }
  const withinWdWindow = isWithdrawInWindow();

  const canSubmit = amountValid && !!file && !loading;

  const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
  const ALLOWED_EXT = /\.(jpe?g|png|webp)$/i;

  function handleFile(f: File | null) {
    setFileError(null);
    if (filePreview) URL.revokeObjectURL(filePreview);
    if (!f) {
      setFile(null);
      setFilePreview(null);
      return;
    }
    const okType = ALLOWED_TYPES.includes(f.type.toLowerCase()) || ALLOWED_EXT.test(f.name);
    if (!okType) {
      setFile(null);
      setFilePreview(null);
      setFileError("Only JPG, JPEG, PNG or WEBP images are allowed.");
      return;
    }
    setFile(f);
    setFilePreview(URL.createObjectURL(f));
  }

  async function submitWithdraw() {
    if (loading) return;
    if (!withdrawEnabled) return toast.error(wd?.closed_message || "Withdrawals are currently closed.");
    if (!withinWdWindow)
      return toast.error(`Withdrawal is closed now. Please request between ${wdOpenStr} and ${wdCloseStr} (IST).`);
    const n = Number(amount);
    if (!n || n < min || n > max) return toast.error(`Amount must be between ₹${min} and ₹${max}`);
    if (n > Number((profile as any)?.winning_balance ?? 0))
      return toast.error("Insufficient withdrawable balance. Only winnings can be withdrawn.");
    if (!file) return toast.error("Please upload your payment QR code to continue.");
    setLoading(true);
    const path = `${profile!.id}/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
    const { error: upErr } = await supabase.storage.from("payment-proofs").upload(path, file);
    if (upErr) {
      setLoading(false);
      return toast.error(upErr.message);
    }
    const proof_path = path;
    const bank =
      bankName || accountName || accountNumber || ifsc
        ? {
            bank_name: bankName.trim() || null,
            account_name: accountName.trim() || null,
            account_number: accountNumber.trim() || null,
            ifsc: ifsc.trim().toUpperCase() || null,
          }
        : null;
    const { error } = await supabase.rpc("request_withdrawal" as any, {
      _amount: n,
      _upi_id: upiId.trim() || null,
      _utr: utr.trim() || null,
      _proof_path: proof_path,
      _bank: bank,
    } as any);
    setLoading(false);
    if (error) return toast.error(error.message);
    toast.success("Request submitted. An admin will approve shortly.");
    onClose();
  }

  return (
    <DialogContent className="max-w-sm rounded-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Withdraw</DialogTitle>
      </DialogHeader>
      <div className="space-y-3">
        {!withdrawEnabled ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
            {wd?.closed_message || "Withdrawals are currently closed."}
          </div>
        ) : (
          <div
            className={`rounded-lg border p-2 text-xs ${
              withinWdWindow
                ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300"
                : "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300"
            }`}
          >
            <span className="font-semibold">Withdraw time (IST):</span> {wdOpenStr || "--:--"} to{" "}
            {wdCloseStr || "--:--"}
            {!withinWdWindow && (
              <div className="mt-1">
                Withdrawal is closed now. Please request between {wdOpenStr} and {wdCloseStr}.
              </div>
            )}
          </div>
        )}
        <div>
          <Label>Amount (₹)</Label>
          <Input
            inputMode="numeric"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))}
            placeholder={`${min} - ${max}`}
            className="h-12 text-lg"
          />
        </div>
        <div className="grid grid-cols-4 gap-2">
          {[100, 500, 1000, 2000].map((v) => (
            <button
              key={v}
              onClick={() => setAmount(String(v))}
              className="rounded-lg border border-input py-2 text-xs font-semibold hover:bg-accent"
            >
              ₹{v}
            </button>
          ))}
        </div>

        <div className="rounded-lg border border-primary/30 bg-primary/5 p-2 text-[11px] text-muted-foreground">
          Provide your payment receiving details. <b>QR Code is required.</b> UPI ID and Bank details are optional.
        </div>

        <div>
          <Label>
            Upload QR Code <span className="text-destructive">(Required)</span>
          </Label>
          <Input
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
          />
          <p className="mt-1 text-[10px] text-muted-foreground">
            Upload your payment receiving QR code. JPG, JPEG, PNG or WEBP only.
          </p>
          {fileError && <p className="mt-1 text-[11px] text-destructive">{fileError}</p>}
          {!file && !fileError && (
            <p className="mt-1 text-[11px] text-destructive">Please upload your payment QR code to continue.</p>
          )}
          {filePreview && (
            <div className="mt-2">
              <img
                src={filePreview}
                alt="QR preview"
                className="max-h-48 w-full rounded-lg border border-border object-contain bg-muted/30"
              />
            </div>
          )}
        </div>

        <div>
          <Label>UPI ID (Optional)</Label>
          <Input value={upiId} onChange={(e) => setUpiId(e.target.value)} placeholder="yourname@bank" />
        </div>

        <details className="rounded-lg border border-input p-2">
          <summary className="cursor-pointer text-xs font-semibold">Bank Details (Optional)</summary>
          <div className="mt-2 space-y-2">
            <div>
              <Label>Account Holder Name</Label>
              <Input value={accountName} onChange={(e) => setAccountName(e.target.value)} placeholder="Full name" />
            </div>
            <div>
              <Label>Bank Name</Label>
              <Input value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="e.g. HDFC Bank" />
            </div>
            <div>
              <Label>Account Number</Label>
              <Input
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ""))}
                placeholder="Account number"
              />
            </div>
            <div>
              <Label>IFSC Code</Label>
              <Input value={ifsc} onChange={(e) => setIfsc(e.target.value.toUpperCase())} placeholder="IFSC" />
            </div>
          </div>
        </details>
        <div>
          <Label>Reference UTR (optional)</Label>
          <Input value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="Any reference" />
        </div>
        <Button
          onClick={submitWithdraw}
          disabled={!canSubmit || !withdrawEnabled || !withinWdWindow}
          className="h-12 w-full gradient-primary text-base font-bold shadow-purple"
        >
          {loading
            ? "Submitting..."
            : !withdrawEnabled
              ? "Withdrawals Closed"
              : !withinWdWindow
                ? "Outside Withdraw Time"
                : !file
                  ? "Upload QR to Continue"
                  : "Request Withdraw"}
        </Button>
      </div>
    </DialogContent>
  );
}
